import { maybe, Maybe, rd } from '@passionware/monads';
import { SHELL_FEATURE_FLAGS } from 'v5/core/app-scope/shellFeatureFlags';
import { AtellioFeatureGroupDefinition } from 'v5/features';
import {
  FeatureName,
  FeatureValueById
} from 'v5/platform/feature-management/feature-management';
import { WithFeatureManagementService } from 'v5/services/front/FeatureManagementService/FeatureManagementService';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { MergeServices } from 'v5/platform/ts/services';

export interface FeatureVersionToggle {
  isNewVersion: boolean;
  /** Saves the choice; `successToast: false` when the caller confirms it itself. */
  switchVersion: (
    useNewVersion: boolean,
    options?: { successToast?: boolean }
  ) => void;
}

/**
 * Lets the user flip an enum feature flag between two of its versions.
 * Absent while the flag is loading, enforced, or set to any other version.
 */
export function useFeatureVersionToggle<
  Feature extends FeatureName<AtellioFeatureGroupDefinition>
>({
  services,
  featureName,
  newVersionName,
  rollbackVersionName
}: {
  services: MergeServices<
    [WithFeatureService, WithFeatureManagementService, WithAppScopeService]
  >;
  featureName: Feature;
  newVersionName: FeatureValueById<AtellioFeatureGroupDefinition, Feature>;
  rollbackVersionName: FeatureValueById<AtellioFeatureGroupDefinition, Feature>;
}): Maybe<FeatureVersionToggle> {
  const browsingScope = services.appScopeService.useResolvedScopeIdentity();
  const organizationScope =
    services.appScopeService.useOrganizationScopeIdentity();
  const scope = SHELL_FEATURE_FLAGS.has(featureName)
    ? organizationScope
    : browsingScope;
  const feature = services.featureService.useFeature(scope, featureName);

  if (rd.isError(feature)) {
    console.error('useFeatureVersionToggle: feature is not available', feature);
  }
  if (!rd.isSuccess(feature)) {
    return maybe.ofAbsent();
  }

  if (
    maybe.isPresent(
      services.featureManagementService.getEnforcedFeatureFlagConfig()[
        featureName
      ]
    ) ||
    !services.featureManagementService.isChangeAllowed(featureName)
  ) {
    return maybe.ofAbsent();
  }

  const version = feature.data;

  if (version !== newVersionName && version !== rollbackVersionName) {
    return maybe.ofAbsent();
  }

  return {
    isNewVersion: version === newVersionName,
    switchVersion: (useNewVersion, { successToast = true } = {}) => {
      services.featureManagementService.scheduleAction({
        type: 'setFeatureFlag',
        source: 'frontend',
        featureId: featureName,
        value: useNewVersion ? newVersionName : rollbackVersionName
      });
      void services.featureManagementService.saveConfig({ successToast });
    }
  };
}
